import React from 'react';
import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';

import TopStrip from '../components/TopStrip';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import HelpButton from '../components/HelpButton';
import TestimonialsCarousel from '../components/TestimonialsCarousel';

import heroBanner from '../assets/images/hero-images/technology partnership.webp';
import heroMobile from '../assets/images/mobile-banners/technology-mobile.webp';
import downloadImg from '../assets/images/webp-images/download-section-img.webp';
import companyPdf from '../assets/pdfs/DDevOps-Company-Profile.pdf';

import efficientImg from '../assets/images/tech-page/Efficient.27f25754.png';
import managementImg from '../assets/images/tech-page/managment2.70107d5d.png';
import multiImg from '../assets/images/tech-page/multi1.7713b5e4.png';
import highlyImg from '../assets/images/tech-page/highly.db1a23e4.png';
import integrationImg from '../assets/images/tech-page/integration.601a5b60.png';
import qualityImg from '../assets/images/tech-page/quality.2269cb8e.png';

import logo1 from '../assets/images/logo-slide/1.avif';
import logo2 from '../assets/images/logo-slide/2.avif';
import logo3 from '../assets/images/logo-slide/3.avif';
import logo4 from '../assets/images/logo-slide/4.avif';
import logo5 from '../assets/images/logo-slide/5.avif';
import logo6 from '../assets/images/logo-slide/6.avif';
import logo7 from '../assets/images/logo-slide/7.avif';
import logo8 from '../assets/images/logo-slide/8.avif';
import logo9 from '../assets/images/logo-slide/9.avif';

const logoImages = [logo1,logo2,logo3,logo4,logo5,logo6,logo7,logo8,logo9];

const advantages = [
  { img:efficientImg,   title:'Enterprise-Grade Security',         desc:'Every integration is stress-tested in a sandboxed environment before touching your production systems. Our security reviews cover API authentication, data encryption, and threat modeling.' },
  { img:managementImg,  title:'Strategic Operational Alignment',   desc:'We align our engineering resources with your product roadmap. Monthly syncs and shared dashboards keep us on the same page, all the time.' },
  { img:multiImg,       title:'Full-Cycle Automation',             desc:'We connect your sales, marketing, and ops tools into a unified platform — automated workflows that reduce human error and accelerate every business process.' },
  { img:highlyImg,      title:'Your Extended DevOps Team',         desc:'We do not just provide tools; we provide engineers. You get dedicated specialists embedded in your operations, fully accountable to your SLAs and business outcomes.' },
  { img:integrationImg, title:'Deep Infrastructure Integration',   desc:'We go beyond surface-level API calls. Our engineers integrate at the infrastructure level — database replication, network peering, and custom data pipelines.' },
  { img:qualityImg,     title:'Continuous Quality Assurance',      desc:'Automated regression testing and performance benchmarking run on every deployment, ensuring the integration never degrades over time as your platform evolves.' },
];

const engineServices = [
  { title:'Solution Architecture',        desc:'We design end-to-end technical blueprints that map your business goals to scalable, cloud-native infrastructure and integration patterns.' },
  { title:'24/7 Managed Operations',      desc:'Our NOC monitors your joint infrastructure around the clock, responding to incidents before they impact your shared customer base.' },
  { title:'Data Sovereignty & Compliance',desc:'We implement data residency controls and compliance frameworks (GDPR, SOC2) that protect both partners and their clients.' },
  { title:'Cloud Migration Strategy',     desc:'Migrate monolithic systems to modern cloud architectures with minimal risk using our proven Blue/Green migration playbooks.' },
  { title:'Capacity Planning',            desc:'We forecast infrastructure needs based on your growth trajectory, ensuring you scale proactively — not reactively.' },
  { title:'Advanced Threat Defense',      desc:'Continuous vulnerability scanning, penetration testing schedules, and zero-trust policy implementation across your shared stack.' },
];

function TechnologyPartnership() {
  const logoTrackRef = useRef(null);
  const animRef = useRef(null);
  const posRef = useRef(0);

  useEffect(() => {
    const track = logoTrackRef.current;
    if (!track) return;
    const run = () => {
      posRef.current -= 0.5;
      if (Math.abs(posRef.current) >= track.scrollWidth / 2) posRef.current = 0;
      track.style.left = posRef.current + 'px';
      animRef.current = requestAnimationFrame(run);
    };
    animRef.current = requestAnimationFrame(run);
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  return (
    <>
      <TopStrip />
      <Navbar />

      {/* HERO */}
      <section style={{ marginTop:'125px' }}>
        <div className="container-fluid p-0">
          <img src={heroBanner} width="1600" height="520" alt="Technology Partnership" className="img-fluid w-100 d-none d-md-block" />
          <img src={heroMobile} width="768" height="1000" alt="Technology Partnership mobile" className="img-fluid w-100 d-md-none d-block" />
        </div>
      </section>

      {/* INTRO */}
      <section className="my-5">
        <div className="container">
          <div className="row align-items-center gy-4">
            <div className="col-lg-12 text-center">
              <h2 className="text-uppercase fw-bold text-center" data-aos="fade-down">
                Build More. <span className="txt-color">Worry Less.</span>
              </h2>
            </div>
            <div className="col-lg-12">
              <h3 className="text-md-start text-center ps-0 fs-6 fs-md-5" data-aos="fade-up">
                A technology partnership with DDevOps isn't a vendor relationship — it's a strategic alliance. We embed our engineering capabilities directly into your product and operations, giving you the unfair advantage of a fully staffed DevOps organization without the overhead of building one. We handle the operational complexity so you can focus on the innovation that differentiates your product in the market.
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU GAIN */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold text-uppercase">What You <span className="txt-color">Gain</span></h2>
          </div>
          <div className="row g-4">
            {[
              { title:'Validated Integrations', desc:'We test every integration in staging before production. No surprises, no broken releases. Your users experience a seamless product, always.' },
              { title:'Market Expansion',        desc:'Leverage our network and expertise to enter new markets confidently. Our infrastructure scales across regions, so your growth is never bottlenecked by operations.' },
              { title:'Enhanced Customer Success',desc:'Our 24/7 operations team becomes an extension of your customer success team. Faster resolutions, proactive monitoring, and a healthier product mean happier customers.' },
            ].map((c,i) => (
              <div className="col-md-4" key={i} data-aos="fade-up" data-aos-delay={i*100}>
                <div className="feature-card h-100">
                  <h5 className="fw-semibold mb-3">{c.title}</h5>
                  <p className="text-muted small">{c.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERSHIP TYPES */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold text-uppercase">Partnership <span className="txt-color">Models</span></h2>
          </div>
          <div className="row g-4">
            <div className="col-md-6" data-aos="fade-right">
              <div className="p-4 border rounded shadow-sm h-100">
                <h5 className="fw-bold mb-1">TYPE 1 — Strategic ISV Partnership</h5>
                <p className="text-muted small mb-3">For Independent Software Vendors looking to strengthen their product's infrastructure story.</p>
                <ul>
                  <li>Joint solution development and co-engineering</li>
                  <li>Shared go-to-market programs</li>
                  <li>Technical validation and joint reference architecture</li>
                  <li>Co-branded solution documentation</li>
                </ul>
              </div>
            </div>
            <div className="col-md-6" data-aos="fade-left">
              <div className="p-4 border rounded shadow-sm h-100">
                <h5 className="fw-bold mb-1">TYPE 2 — Managed Services Extension</h5>
                <p className="text-muted small mb-3">For companies that need operational depth without building a full internal DevOps team.</p>
                <ul>
                  <li>Named engineering team assigned to your account</li>
                  <li>Infrastructure managed under your brand</li>
                  <li>Shared SLA accountability</li>
                  <li>Monthly business reviews and roadmap alignment</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PARTNERSHIP PROCESS */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold text-uppercase">How We <span className="txt-color">Partner</span></h2>
          </div>
          <div className="row g-4">
            {[
              { step:'01', title:'Discovery & Alignment', desc:'We start by deeply understanding your product, customers, and growth trajectory. A partnership architect maps out where our capabilities can create the most value for your specific situation.' },
              { step:'02', title:'Technical Validation',  desc:'Our engineers perform a comprehensive audit of your current stack. We identify integration points, risks, and quick wins — then design a technical roadmap for the partnership.' },
              { step:'03', title:'Joint Go-To-Market',    desc:'With the technical foundation set, we align on sales enablement, case studies, and co-marketing assets. We grow together, tracking shared KPIs from day one.' },
            ].map((c,i) => (
              <div className="col-md-4" key={i} data-aos="fade-up" data-aos-delay={i*100}>
                <div className="workflow-card h-100">
                  <div className="step mb-2">STEP {c.step}</div>
                  <h5 className="fw-bold mb-2">{c.title}</h5>
                  <p className="text-muted small">{c.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERSHIP ADVANTAGE (6 image cards) */}
      <section className="py-5 px-3 bg-light">
        <div className="container">
          <h2 className="text-center mb-5 text-uppercase fw-bold top-line">The Partnership <span className="text-info">Advantage</span></h2>
          <div className="row g-4">
            {advantages.map((a,i) => (
              <div className="col-md-6 col-lg-4" key={i} data-aos="fade-up" data-aos-delay={i*80}>
                <div className="feature-card h-100 text-center">
                  <img src={a.img} loading="lazy" className="img-fluid mb-3" style={{ maxHeight:'120px', objectFit:'contain' }} alt={a.title} />
                  <h5 className="fw-bold mt-3 fs-5">{a.title}</h5>
                  <p className="text-muted small">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA DARK */}
      <section className="py-5 bg-dark bg-gradient">
        <div className="container text-center text-white">
          <h2 className="fw-bold mb-3">Ready to <span className="text-info">Partner</span> With the Best?</h2>
          <p className="mb-4 text-white-50">Join our growing network of technology partners. Schedule a call with our partnership team today.</p>
          <Link to="/contact" className="btn btn-info text-white px-5 py-3 fw-bold">Start a Partnership Conversation</Link>
        </div>
      </section>

      {/* ENGINE BEHIND GROWTH */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="text-uppercase fw-semibold mb-3">The Engine Behind Your <span className="text-info">Growth</span></h2>
          </div>
          <div className="row g-4">
            {engineServices.map((s,i) => (
              <div className="col-md-6 col-lg-4" key={i} data-aos="fade-up" data-aos-delay={i*80}>
                <div className="lifecycle-card h-100">
                  <h5 className="fw-semibold mb-3 text-info">{s.title}</h5>
                  <p className="text-muted small">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DOWNLOAD */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="row align-items-center gy-4">
            <div className="col-lg-6" data-aos="fade-right">
              <h4 className="fw-semibold mb-3 top-line">Download our Partnership Deck</h4>
              <p className="text-muted mb-4">Get the full technical overview of our partnership program, case studies, and integration capabilities.</p>
              <h2 className="fw-bold mb-3">Engineering <span className="txt-color">Excellence</span> — Together.</h2>
              <a href={companyPdf} target="_blank" rel="noreferrer" className="btn btn-info text-white fw-bold px-4 py-3">Download Partnership Deck</a>
            </div>
            <div className="col-lg-6 text-center" data-aos="fade-left">
              <img src={downloadImg} loading="lazy" alt="Partnership Deck" className="img-fluid floating-icon mt-4" style={{ maxHeight:'350px', objectFit:'contain' }} />
            </div>
          </div>
        </div>
      </section>

      {/* SECOND DARK CTA */}
      <section className="py-5 bg-dark bg-gradient">
        <div className="container text-center text-white">
          <h2 className="fw-bold mb-2">Not sure which model fits? <span className="text-info">Let's talk.</span></h2>
          <p className="text-white-50 mb-4">Our partnership architects will help you find the right structure for your business.</p>
          <Link to="/contact" className="btn btn-outline-light px-5 py-3">Book a Free Consultation</Link>
        </div>
      </section>

      {/* LOGO SLIDER */}
      <section className="logo-slider py-1 bg-white overflow-hidden mt-5">
        <h2 className="text-center text-uppercase top-line">The <span className="text-info fw-semibold">DDevOps</span> Toolkit</h2>
        <div className="container" style={{ margin:'1rem auto' }}>
          <div className="logo-wrapper">
            <div className="logo-track" ref={logoTrackRef} style={{ position:'relative' }}>
              {[...logoImages,...logoImages].map((img,i) => (
                <img key={i} src={img} loading="lazy" className="slide-logo-img" alt={`Logo ${(i%9)+1}`} height="60" />
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <HelpButton />
    </>
  );
}

export default TechnologyPartnership;
